'use client';

import { CardBlock } from '@modules/prism-client/components/content-blocks/Card/CardBlock';
import { telemetryToClickAttributes } from '@modules/prism-client/lib/telemetry';
import { RelevantInterestingSection } from '@tuic/web-compositions/my-trips/RelevantInterestingSection';

import type { CardBlockData } from '@modules/prism-client/components/content-blocks/Card/CardBlock';
import type PrismAPI from '@modules/prism-client/types/prism-types';
import type { ComponentBlock } from '@modules/prism-client/types/types';
import type {
  RelevantInterestingSectionCategory,
  RelevantInterestingSectionCardItem,
} from '@tuic/web-compositions/my-trips/RelevantInterestingSection';
import type { ReactNode } from 'react';

type Section = {
  header?: {
    headline?: string;
    subline?: ReactNode;
  };
  cards?: CardBlockData[];
  telemetry?: PrismAPI.TelemetryInstruction[];
};

export type RelevantInterestingSectionBlockData = {
  fallback?: boolean;
  relevant?: Section;
  interesting?: Section;
};

export const RelevantInterestingSectionBlock: ComponentBlock<
  RelevantInterestingSectionBlockData
> = ({ data, fallback }) => {
  if (fallback) {
    return <RelevantInterestingSection fallback />;
  }

  const { relevant, interesting } = data ?? {};

  // Build categories from relevant and interesting sections
  const categories: RelevantInterestingSectionCategory[] = [
    relevant,
    interesting,
  ]
    .filter((section): section is Section => !!section && !!section.cards)
    .map(section => {
      // Transform CardBlockData to RelevantInterestingSectionCardItem
      const items: RelevantInterestingSectionCardItem[] = (
        section.cards || []
      ).map((cardData, idx) => ({
        card: <CardBlock key={idx} data={cardData} />,
        flexMobile: cardData.card?.flexMobile,
        flexTabletAndDesktop: cardData.card?.flexTabletAndDesktop,
      }));

      return {
        header: section.header,
        items,
        segmentedControlProps: {
          label: section.header?.headline,
          ...telemetryToClickAttributes(section.telemetry),
        },
      };
    });

  return <RelevantInterestingSection categories={categories} />;
};
